import { useFrame } from '@react-three/fiber';
import {
  Color,
  CylinderGeometry,
  type MeshStandardMaterial,
  Quaternion,
  Vector3,
} from 'three';
import { useEffect, useMemo, useRef } from 'react';
import type { Graph3DEdgeRendererProps } from '../../core/renderer.types';
import type { Graph3DPositionedNode } from '../../core/graph.types';

const up = new Vector3(0, 1, 0);
const HIDDEN_EDGE_COLOR = '#a8b4c7';
const HIDDEN_EDGE_RADIUS = 0.1;
const HIDDEN_EDGE_OPACITY = 0.008;
const FLOW_HEAD_SPEED = 1.02;
const FLOW_TRAIL_SPAN = 0.26;
const FLOW_WAKE_SPAN = 0.54;
const FLOW_BASE_EMISSIVE_INTENSITY = 0.34;
const FLOW_PEAK_EMISSIVE_BOOST = 1.32;
const FLOW_BASE_OPACITY = 0.44;
const FLOW_PEAK_OPACITY_BOOST = 0.46;
const FLOW_SEGMENT_MAX_LENGTH = 6.2;
const EDGE_SEGMENT_DEBUG_PREFIX = '[Graph3D edge perf]';
const EDGE_CYLINDER_GEOMETRY = new CylinderGeometry(1, 1, 1, 8);

function shouldLogEdgeSegmentPerf() {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    (window as Window & { __GRAPH3D_EDGE_SEGMENT_DEBUG__?: boolean })
      .__GRAPH3D_EDGE_SEGMENT_DEBUG__ === true
  );
}

function getGraph3DPerfContext() {
  if (typeof window === 'undefined') {
    return undefined;
  }
  return (
    window as Window & { __GRAPH3D_PERF_CONTEXT__?: Record<string, unknown> }
  ).__GRAPH3D_PERF_CONTEXT__;
}

function getEdgeDataString(data: unknown, key: string, fallback = '') {
  if (!data || typeof data !== 'object') {
    return fallback;
  }
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : fallback;
}

function getEdgeDataBoolean(data: unknown, key: string, fallback = false) {
  if (!data || typeof data !== 'object') {
    return fallback;
  }
  const value = (data as Record<string, unknown>)[key];
  return typeof value === 'boolean' ? value : fallback;
}

function getNodeCenter(node: Graph3DPositionedNode): Vector3 {
  return new Vector3(
    node.resolvedPosition.x,
    node.resolvedPosition.y,
    node.resolvedPosition.z
  );
}

function getNodeBoundaryPoint(
  node: Graph3DPositionedNode,
  toward: Vector3
): Vector3 {
  const center = getNodeCenter(node);
  const direction = toward.clone().sub(center);
  if (direction.lengthSq() === 0) {
    return center;
  }
  const halfWidth = Math.max(node.resolvedSize.width / 2, 1);
  const halfHeight = Math.max(node.resolvedSize.height / 2, 1);
  const ratio = Math.max(
    Math.abs(direction.x) / halfWidth,
    Math.abs(direction.y) / halfHeight,
    0.001
  );
  return center.add(direction.multiplyScalar(1 / ratio));
}

function createSegment(source: Vector3, target: Vector3) {
  const direction = target.clone().sub(source);
  const length = direction.length();
  if (length < 1) {
    return null;
  }
  return {
    source: source.clone(),
    target: target.clone(),
    midpoint: source.clone().add(target).multiplyScalar(0.5),
    length,
    quaternion: new Quaternion().setFromUnitVectors(up, direction.normalize()),
  };
}

function clampRevealProgress(progress: number | undefined) {
  if (typeof progress !== 'number') {
    return 1;
  }
  return Math.min(Math.max(progress, 0), 1);
}

function createFlowSegments(
  source: Vector3,
  target: Vector3,
  maxSegmentLength: number
) {
  const direction = target.clone().sub(source);
  const length = direction.length();
  if (length < 1) {
    return [] as Array<NonNullable<ReturnType<typeof createSegment>>>;
  }
  const segmentCount = Math.max(1, Math.ceil(length / maxSegmentLength));
  const segments = [] as Array<NonNullable<ReturnType<typeof createSegment>>>;
  for (let index = 0; index < segmentCount; index += 1) {
    const startPoint = source.clone().lerp(target, index / segmentCount);
    const endPoint = source.clone().lerp(target, (index + 1) / segmentCount);
    const segment = createSegment(startPoint, endPoint);
    if (segment) {
      segments.push(segment);
    }
  }
  return segments;
}

function getRoutePoints(data: unknown) {
  if (!data || typeof data !== 'object') {
    return [] as Array<{ x: number; y: number; z: number }>;
  }
  const value = (data as { routePoints?: unknown }).routePoints;
  if (!Array.isArray(value)) {
    return [] as Array<{ x: number; y: number; z: number }>;
  }
  return value.filter(
    point =>
      !!point &&
      typeof point === 'object' &&
      typeof (point as { x?: unknown }).x === 'number' &&
      typeof (point as { y?: unknown }).y === 'number' &&
      typeof (point as { z?: unknown }).z === 'number'
  ) as Array<{ x: number; y: number; z: number }>;
}

// ─── 跨楼层边自动曲线参数 ───
// 当边在 y 轴上的跨度超过此阈值时，自动生成贝塞尔曲线中间点
const CURVE_MIN_VERTICAL_SPAN = 80;
// 控制点偏移量与边长的比例
const CURVE_CONTROL_OFFSET_RATIO = 0.1;
// 控制点偏移量上限，避免过弯
const CURVE_CONTROL_OFFSET_MAX = 50;
// 贝塞尔曲线插值点数（点越多曲线越平滑，但圆柱段也越多）
const CURVE_INTERPOLATION_COUNT = 8;

/**
 * 为跨楼层（大垂直跨度）的边自动生成二次贝塞尔曲线路由点。
 * 弯曲方向优先取从原点指向边中点的径向方向（向外弯），
 * 使楼板间的连线呈现自然弧形而非死板直线。
 */
function generateCurveRoutePoints(source: Vector3, target: Vector3): Vector3[] {
  const direction = target.clone().sub(source);
  const length = direction.length();
  if (length < CURVE_MIN_VERTICAL_SPAN) {
    return [];
  }

  const verticalSpan = Math.abs(direction.y);
  if (verticalSpan < CURVE_MIN_VERTICAL_SPAN) {
    return [];
  }

  const midpoint = source.clone().add(target).multiplyScalar(0.5);

  // 弯曲方向：优先使用从原点指向边中点的径向方向（向外弯）
  let bowDirection = new Vector3(midpoint.x, 0, midpoint.z);
  if (bowDirection.lengthSq() < 0.01) {
    // 中点在原点附近时，退化为边水平分量的垂直方向
    const horizontalDir = new Vector3(direction.x, 0, direction.z);
    if (horizontalDir.lengthSq() < 0.01) {
      bowDirection.set(1, 0, 0);
    } else {
      horizontalDir.normalize();
      bowDirection.set(-horizontalDir.z, 0, horizontalDir.x);
    }
  }
  bowDirection.normalize();

  // 控制点偏移量：与边长成正比，但有上限
  const offset = Math.min(
    length * CURVE_CONTROL_OFFSET_RATIO,
    CURVE_CONTROL_OFFSET_MAX
  );
  const controlPoint = midpoint
    .clone()
    .add(bowDirection.multiplyScalar(offset));

  // 沿二次贝塞尔曲线生成中间点
  const points: Vector3[] = [];
  for (let i = 1; i <= CURVE_INTERPOLATION_COUNT; i += 1) {
    const t = i / (CURVE_INTERPOLATION_COUNT + 1);
    const inv = 1 - t;
    points.push(
      source
        .clone()
        .multiplyScalar(inv * inv)
        .add(controlPoint.clone().multiplyScalar(2 * inv * t))
        .add(target.clone().multiplyScalar(t * t))
    );
  }

  return points;
}

function getEdgePhase(edgeId: string) {
  const hash = Array.from(edgeId).reduce(
    (sum, char) => sum + char.charCodeAt(0),
    0
  );
  return (hash % 100) / 100;
}

export function ThinHierarchyEdge3D({
  edge,
  runtime,
  theme,
}: Graph3DEdgeRendererProps) {
  const edgeColor = getEdgeDataString(
    edge.data,
    'themeColor',
    theme.edge.color
  );
  const sourceCenter = useMemo(
    () => getNodeCenter(edge.sourceNode),
    [edge.sourceNode]
  );
  const targetCenter = useMemo(
    () => getNodeCenter(edge.targetNode),
    [edge.targetNode]
  );
  const start = useMemo(
    () => getNodeBoundaryPoint(edge.sourceNode, targetCenter),
    [edge.sourceNode, targetCenter]
  );
  const end = useMemo(
    () => getNodeBoundaryPoint(edge.targetNode, sourceCenter),
    [edge.targetNode, sourceCenter]
  );
  const routePoints = useMemo(() => getRoutePoints(edge.data), [edge.data]);
  const isFlowing = runtime?.flow === true && runtime?.hidden !== true;
  // 允许通过 edge data 的 curveStyle='none' 显式禁用自动曲线
  const curveStyle = getEdgeDataString(edge.data, 'curveStyle', 'auto');
  const segments = useMemo(() => {
    const explicitRoutePoints = routePoints.map(
      point => new Vector3(point.x, point.y, point.z)
    );
    // 如果没有显式路由点且未禁用曲线，自动为跨楼层边生成贝塞尔曲线中间点
    const autoCurvePoints =
      explicitRoutePoints.length === 0 && curveStyle !== 'none'
        ? generateCurveRoutePoints(start, end)
        : [];
    const points = [start, ...explicitRoutePoints, ...autoCurvePoints, end];
    const lines = [] as Array<NonNullable<ReturnType<typeof createSegment>>>;
    for (let index = 0; index < points.length - 1; index += 1) {
      if (isFlowing) {
        lines.push(
          ...createFlowSegments(
            points[index],
            points[index + 1],
            FLOW_SEGMENT_MAX_LENGTH
          )
        );
        continue;
      }
      const segment = createSegment(points[index], points[index + 1]);
      if (segment) {
        lines.push(segment);
      }
    }
    return lines;
  }, [curveStyle, end, isFlowing, routePoints, start]);
  const segmentProgresses = useMemo(() => {
    const totalLength = segments.reduce(
      (sum, segment) => sum + segment.length,
      0
    );
    if (totalLength <= 0) {
      return segments.map(() => 0);
    }
    let elapsedLength = 0;
    return segments.map(segment => {
      const progress = (elapsedLength + segment.length / 2) / totalLength;
      elapsedLength += segment.length;
      return progress;
    });
  }, [segments]);
  const segmentRanges = useMemo(() => {
    const totalLength = segments.reduce(
      (sum, segment) => sum + segment.length,
      0
    );
    if (totalLength <= 0) {
      return segments.map(() => ({ start: 0, end: 0 }));
    }
    let elapsedLength = 0;
    return segments.map(segment => {
      const start = elapsedLength / totalLength;
      elapsedLength += segment.length;
      return {
        start,
        end: elapsedLength / totalLength,
      };
    });
  }, [segments]);
  const isActive = runtime?.active || runtime?.highlighted;
  const isRunning = runtime?.status === 'running' || runtime?.flow;
  const revealProgress = clampRevealProgress(runtime?.progress);
  const showDefaultThemeColor = getEdgeDataBoolean(
    edge.data,
    'showDefaultThemeColor'
  );
  const materialRefs = useRef<Array<MeshStandardMaterial | null>>([]);
  const edgePhase = useMemo(() => getEdgePhase(edge.id), [edge.id]);
  const color = runtime?.hidden
    ? HIDDEN_EDGE_COLOR
    : (runtime?.color ??
      (isActive || isRunning || showDefaultThemeColor ? edgeColor : '#cbd5e1'));
  const flowColor = useMemo(
    () => new Color(color).lerp(new Color('#7dd3fc'), 0.42),
    [color]
  );
  const flowHotColor = useMemo(
    () => flowColor.clone().lerp(new Color('#ffffff'), 0.78),
    [flowColor]
  );
  const radius = runtime?.hidden
    ? HIDDEN_EDGE_RADIUS
    : isActive
      ? 0.52
      : isRunning
        ? 0.4
        : showDefaultThemeColor
          ? 0.4
          : 0.24;

  useEffect(() => {
    if (!shouldLogEdgeSegmentPerf()) {
      return;
    }
    console.info(EDGE_SEGMENT_DEBUG_PREFIX, 'hierarchy-segments', {
      edgeId: edge.id,
      routePointCount: routePoints.length,
      segmentCount: segments.length,
      flowEnabled: isFlowing,
      hidden: runtime?.hidden === true,
      ...getGraph3DPerfContext(),
    });
  }, [
    edge.id,
    isFlowing,
    routePoints.length,
    runtime?.hidden,
    segments.length,
  ]);

  useFrame(({ clock }) => {
    if (!runtime?.flow || runtime?.hidden || segmentProgresses.length === 0) {
      return;
    }
    const head = (clock.elapsedTime * FLOW_HEAD_SPEED + edgePhase) % 1;
    materialRefs.current.forEach((material, index) => {
      if (!material) {
        return;
      }
      const progress = segmentProgresses[index] ?? 0;
      const distance = head >= progress ? head - progress : head - progress + 1;
      const pulse =
        distance <= FLOW_TRAIL_SPAN ? 1 - distance / FLOW_TRAIL_SPAN : 0;
      const wake =
        distance <= FLOW_WAKE_SPAN ? 1 - distance / FLOW_WAKE_SPAN : 0;
      const blend = Math.min(1, pulse + wake * 0.52);
      material.color.copy(flowColor).lerp(flowHotColor, blend);
      material.emissive
        .copy(flowColor)
        .lerp(flowHotColor, Math.min(1, blend * 1.08));
      material.emissiveIntensity =
        FLOW_BASE_EMISSIVE_INTENSITY +
        pulse * FLOW_PEAK_EMISSIVE_BOOST +
        wake * 0.28;
      material.opacity = Math.min(
        0.96,
        FLOW_BASE_OPACITY + pulse * FLOW_PEAK_OPACITY_BOOST + wake * 0.18
      );
    });
  });

  if (segments.length === 0) {
    return null;
  }

  return (
    <group>
      {segments.map((segment, index) => {
        const range = segmentRanges[index] ?? { start: 0, end: 1 };
        if (!runtime?.flow && revealProgress <= range.start) {
          return null;
        }
        let segmentLength = segment.length;
        let segmentPosition = segment.midpoint;
        if (!runtime?.flow && revealProgress < range.end) {
          const localProgress =
            range.end > range.start
              ? (revealProgress - range.start) / (range.end - range.start)
              : 0;
          const clampedLocalProgress = Math.min(Math.max(localProgress, 0), 1);
          const direction = segment.target
            .clone()
            .sub(segment.source)
            .normalize();
          segmentLength = Math.max(
            segment.length * clampedLocalProgress,
            0.001
          );
          segmentPosition = segment.source
            .clone()
            .add(direction.multiplyScalar(segmentLength / 2));
        }
        return (
          <mesh
            key={`${edge.id}-${index}`}
            position={segmentPosition}
            quaternion={segment.quaternion}
            scale={[radius, segmentLength, radius]}
          >
            <primitive object={EDGE_CYLINDER_GEOMETRY} attach="geometry" />
            <meshStandardMaterial
              ref={material => {
                materialRefs.current[index] = material;
              }}
              color={runtime?.flow && !runtime?.hidden ? flowColor : color}
              emissive={runtime?.flow && !runtime?.hidden ? flowColor : color}
              emissiveIntensity={
                runtime?.hidden
                  ? 0
                  : runtime?.flow
                    ? FLOW_BASE_EMISSIVE_INTENSITY
                    : isActive
                      ? 0.08
                      : showDefaultThemeColor
                        ? 0.16
                        : 0.01
              }
              transparent
              opacity={
                runtime?.hidden
                  ? HIDDEN_EDGE_OPACITY
                  : runtime?.flow
                    ? FLOW_BASE_OPACITY
                    : isActive
                      ? 0.68
                      : isRunning
                        ? 0.34
                        : showDefaultThemeColor
                          ? 0.78
                          : 0.18
              }
              roughness={0.96}
              metalness={0}
            />
          </mesh>
        );
      })}
    </group>
  );
}
